import { useMemo, useRef } from 'react'

import { RequirementValue } from '@makyoapp/makyo-sdk/api/Organizations'
import { IPluginFrontend, IRequirement, PluginFieldType, RequirementType } from '@makyoapp/makyo-sdk/models/Plugins'
import { OpenNew16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant } from '@makyoapp/makyo-ui'

import NumberField from './Number'
import SelectField from './Select'
import TextField from './TextField'

export interface PluginRequirementsProps {
  /**
   * backend requirements
   */
  backend?: IRequirement[]
  /**
   * frontend requirements
   */
  frontend?: IPluginFrontend
  /**
   * saved backend requirements
   */
  backendRequirements?: RequirementValue
  onChange(backend: Record<string, string>, frontend: Record<string, string>): void
}

export interface RequirementFieldProps {
  requirement: IRequirement
  onValue: (value: string) => void
}

export interface RequirementProps {
  requirement: IRequirement
  lang: string
  onValue: (value: string) => void
  value?: any
}

export function RequirementLink({ requirement }: { requirement: IRequirement }) {
  return (
    <>
      {requirement._Link !== '' && (
        <Button
          variant={ButtonVariant.Ghost}
          icon={<OpenNew16 />}
          onClick={() => window.open(requirement._Link, '_blank')}
        />
      )}
    </>
  )
}

function Requirement({ requirement, lang, onValue, value }: RequirementProps) {
  if (requirement._Type !== RequirementType.User) {
    return
  }

  // TODO: handle RequirementType.Plugin. This type of requirement means that it is rendered by the plugin - a good example is google sheets plugin

  switch (requirement._FieldType) {
    case PluginFieldType.Text:
      return <TextField requirement={requirement} onValue={onValue} value={value} />
    case PluginFieldType.Email:
      return <TextField type='email' requirement={requirement} onValue={onValue} value={value} />
    case PluginFieldType.Password:
      return <TextField type='password' requirement={requirement} onValue={onValue} value={value} />
    case PluginFieldType.Number:
      return <NumberField requirement={requirement} onValue={onValue} value={value} />
    case PluginFieldType.Select:
      return <SelectField requirement={requirement} onValue={onValue} value={value} />
    default:
      return <TextField type='password' requirement={requirement} onValue={onValue} value={value} />
  }
}

export default function PluginRequirements({ backend, backendRequirements, onChange }: PluginRequirementsProps) {
  // TODO: get current user language
  const lang = useMemo(() => 'en', [])

  // ─── Refs ────────────────────────────────────────────────────────────────────
  const backendForm = useRef<Record<string, any>>({})

  // ─── Functions ───────────────────────────────────────────────────────────────
  const handleValue = (key: string, value: string) => {
    backendForm.current[key] = value
    onChange(backendForm.current, {})
  }

  if (backend?.length === 0) {
    return null
  }

  return (
    <div className='flex flex-col gap-2'>
      <h4>Requirements</h4>
      <div className='mt-2 flex flex-col gap-1'>
        {backend?.map((req) => (
          <div key={req._Key}>
            <Requirement
              requirement={req}
              lang={lang}
              onValue={(v) => handleValue(req._Key, v)}
              value={backendRequirements ? backendRequirements[req._Key] : undefined}
            />
          </div>
        ))}
      </div>
      {/* <Button className='self-start'>Test</Button> */}
    </div>
  )
}
